import moment from "moment"
import {
  FALLBACK_CUSTOMER_INSTALMENTS,
  INSTALMENT_STATUS,
  INSTALMENT_TYPE,
  INSTALMENT_VIEW,
  SCHEME_STATUS,
} from "./constants"
import { toAmount } from "./format"

const byInstalmentNo = (a, b) => a.installmentNo - b.installmentNo

const ofType = (scheme, type) =>
  (scheme?.installments || [])
    .filter((item) => item.type === type)
    .map((item) => ({ ...item, amount: toAmount(item.amount) }))
    .sort(byInstalmentNo)

export const todayISO = () => moment().format("YYYY-MM-DD")

export const getInstalmentView = (instalment, today = todayISO()) => {
  const { status, type, dueDate, graceEndDate } = instalment || {}

  if (status === INSTALMENT_STATUS.CANCELLED) return INSTALMENT_VIEW.CANCELLED

  if (type === INSTALMENT_TYPE.BONUS) {
    return status === INSTALMENT_STATUS.PAID
      ? INSTALMENT_VIEW.BONUS_CREDITED
      : INSTALMENT_VIEW.BONUS_PENDING
  }

  if (status === INSTALMENT_STATUS.PAID) return INSTALMENT_VIEW.PAID
  if (status === INSTALMENT_STATUS.MISSED) return INSTALMENT_VIEW.MISSED

  if (status === INSTALMENT_STATUS.DUE) {
    if (dueDate > today) return INSTALMENT_VIEW.SCHEDULED
    if (dueDate === today) return INSTALMENT_VIEW.DUE_NOW
    if (graceEndDate && today > graceEndDate) return INSTALMENT_VIEW.OVERDUE
    return INSTALMENT_VIEW.IN_GRACE
  }

  return INSTALMENT_VIEW.UPCOMING
}

const findCurrentDue = (scheme, customerInstalments) => {
  if (
    scheme?.status === SCHEME_STATUS.CANCELLED ||
    scheme?.status === SCHEME_STATUS.MATURED
  ) {
    return null
  }
  return (
    customerInstalments.find(
      (item) => item.status === INSTALMENT_STATUS.DUE
    ) || null
  )
}

const buildMissWarning = (missCount, maxMissed) => {
  if (!missCount) return null
  if (maxMissed && missCount > maxMissed) {
    return `${missCount} instalments missed — the retailer contribution is at risk`
  }
  if (maxMissed) return `${missCount} of ${maxMissed} allowed misses used`
  return `${missCount} instalment${missCount > 1 ? "s" : ""} missed`
}

export const getMaturityValue = (monthlyAmount, master) =>
  toAmount(monthlyAmount) *
  ((master?.customerInstallments || FALLBACK_CUSTOMER_INSTALMENTS) +
    (master?.bonusInstallments || 0))

export const getSchemeMetrics = (scheme) => {
  const customerInstalments = ofType(scheme, INSTALMENT_TYPE.CUSTOMER)
  const bonusInstalments = ofType(scheme, INSTALMENT_TYPE.BONUS)
  const master = scheme?.schemeMaster
  const status = scheme?.status
  const today = todayISO()

  const totalInstalments =
    master?.customerInstallments ||
    customerInstalments.length ||
    FALLBACK_CUSTOMER_INSTALMENTS
  const monthlyAmount = toAmount(scheme?.monthlyAmount)
  const paidCount =
    toAmount(scheme?.paidCount) ||
    customerInstalments.filter(
      (item) => item.status === INSTALMENT_STATUS.PAID
    ).length
  const paidTotal = toAmount(scheme?.paidTotal) || paidCount * monthlyAmount
  const remainingCount = Math.max(0, totalInstalments - paidCount)
  const currentDue = findCurrentDue(scheme, customerInstalments)
  const missCount = toAmount(scheme?.missCount)
  const maxMissed = toAmount(master?.maxMissedInstallments)
  const allInstalments = [...customerInstalments, ...bonusInstalments].sort(
    byInstalmentNo
  )
  const currentDueView = currentDue ? getInstalmentView(currentDue, today) : null
  const bonusCredited = bonusInstalments
    .filter((item) => item.status === INSTALMENT_STATUS.PAID)
    .reduce((sum, item) => sum + item.amount, 0)

  const isClosed =
    status === SCHEME_STATUS.CANCELLED || status === SCHEME_STATUS.MATURED
  const contributionApplies = status !== SCHEME_STATUS.CANCELLED

  const walletValue =
    status === SCHEME_STATUS.CANCELLED
      ? paidTotal
      : status === SCHEME_STATUS.MATURED
      ? paidTotal + bonusCredited
      : getMaturityValue(monthlyAmount, master)

  return {
    totalInstalments,
    paidCount,
    remainingCount,
    monthlyAmount,
    paidTotal,
    remainingAmount: remainingCount * monthlyAmount,
    progressPercent: totalInstalments
      ? Math.round((paidCount / totalInstalments) * 100)
      : 0,
    walletValue,
    isProjectedValue: !isClosed,
    contributionApplies,
    customerInstalments,
    bonusInstalments,
    allInstalments,
    currentDue,
    currentDueView,
    nextDueDate: currentDue?.dueDate || null,
    graceEndsOn: currentDue?.graceEndDate || null,
    isInGrace: currentDueView === INSTALMENT_VIEW.IN_GRACE,
    isOverdue: currentDueView === INSTALMENT_VIEW.OVERDUE,
    isDueNow:
      currentDueView === INSTALMENT_VIEW.DUE_NOW ||
      currentDueView === INSTALMENT_VIEW.IN_GRACE ||
      currentDueView === INSTALMENT_VIEW.OVERDUE,
    maturityDate: allInstalments.length
      ? allInstalments[allInstalments.length - 1].dueDate
      : null,
    isAwaitingActivation:
      status === SCHEME_STATUS.PENDING && paidCount === 0,
    missCount,
    maxMissed,
    missWarning: buildMissWarning(missCount, maxMissed),
  }
}

export const isAmountValid = (amount, master) => {
  const value = toAmount(amount)
  const minAmount = toAmount(master?.minAmount)
  const multipleOf = toAmount(master?.amountMultipleOf)

  if (!value || value < minAmount) return false
  if (multipleOf && value % multipleOf !== 0) return false
  return true
}

export const getAmountError = (amount, master) => {
  const value = toAmount(amount)
  const minAmount = toAmount(master?.minAmount)
  const multipleOf = toAmount(master?.amountMultipleOf)

  if (!value) return "Enter a monthly amount"
  if (value < minAmount) return `Monthly amount must be at least Rs. ${minAmount}`
  if (multipleOf && value % multipleOf !== 0) {
    return `Monthly amount must be in multiples of Rs. ${multipleOf}`
  }
  return null
}
